import { html, LitElement, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { StaticValue, literal, html as staticHtml } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ARIAMixinStrict } from '@material/web/internal/aria/aria.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/iconbutton/filled-icon-button.js';
import '@material/web/iconbutton/filled-tonal-icon-button.js';
import '@material/web/iconbutton/outlined-icon-button.js';
import '@digital-realty/ix-icon/ix-icon.js';
import { IconButton } from '@material/web/iconbutton/internal/icon-button.js';
import { FormSubmitterType } from '@material/web/internal/controller/form-submitter.js';
import { AriaForwardMixin } from '@digital-realty/ix-shared-fns/aria-forward-mixin.js';
import { spread } from '@open-wc/lit-helpers';

type LinkTarget = '_blank' | '_parent' | '_self' | '_top';
export type Appearance =
  | 'filled'
  | 'filled-tonal'
  | 'outlined'
  | 'icon-filled'
  | 'default';

const IxIconButtonBase = AriaForwardMixin(LitElement);

export class IxIconButton extends IxIconButtonBase {
  @query('.button')
  readonly component!: IconButton;

  @property()
  appearance: Appearance = 'default';

  @property()
  icon: string = '';

  @property()
  selectedIcon: string = '';

  /**
   * Disables the icon button and makes it non-interactive.
   */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Flips the icon if it is in an RTL context at startup.
   */
  @property({ type: Boolean, attribute: 'flip-icon-in-rtl' })
  flipIconInRtl = false;

  /**
   * Sets the underlying `HTMLAnchorElement`'s `href` resource attribute.
   */
  @property() href: string | undefined = undefined;

  /**
   * Sets the underlying `HTMLAnchorElement`'s `target` attribute.
   */
  @property() target: LinkTarget | '' | undefined = undefined;

  /**
   * The `aria-label` of the button when the button is toggleable and selected.
   */
  @property({ attribute: 'aria-label-selected' }) ariaLabelSelected:
    | string
    | undefined = undefined;

  /**
   * When true, the button will toggle between selected and unselected
   * states
   */
  @property({ type: Boolean }) toggle = false;

  /**
   * Sets the selected state. When false, displays the default icon. When true,
   * displays the selected icon, or the default icon If no `slot="selected"`
   * icon is provided.
   */
  @property({ type: Boolean, reflect: true }) selected = false;

  @property() type: FormSubmitterType = 'submit';

  @property() name: string | undefined = undefined;

  @property() value: string | undefined = undefined;

  @property() label: string | undefined = undefined;

  @property({ type: Boolean }) filledIcon = false;

  render() {
    const ariaLabel = this.label || (this as ARIAMixinStrict).ariaLabel;

    let tag: StaticValue = literal`md-icon-button`;
    if (this.appearance === 'outlined') {
      tag = literal`md-outlined-icon-button`;
    } else if (this.appearance === 'filled') {
      tag = literal`md-filled-icon-button`;
    } else if (this.appearance === 'filled-tonal') {
      tag = literal`md-filled-tonal-icon-button`;
    }

    return staticHtml`<${tag}
      class="button"
      ?disabled=${this.disabled}
      ?flip-icon-in-rtl=${this.flipIconInRtl}
      href=${ifDefined(this.href)}
      target=${ifDefined(this.target)}
      aria-label-selected=${ifDefined(this.ariaLabelSelected)}
      aria-label=${ariaLabel || nothing}
      ?toggle=${this.toggle}
      ?selected=${this.selected}
      type=${this.type}
      name=${ifDefined(this.name)}
      value=${ifDefined(this.value)}
      ${spread(this.ariaAttributes)}
      >${
        this.icon
          ? html`<ix-icon ?filled=${this.filledIcon}>${this.icon}</ix-icon>`
          : html`<slot name="default"></slot
              ><slot name="selected" slot="selected"></slot>`
      }
      ${
        this.selectedIcon
          ? html`<ix-icon ?filled=${this.filledIcon} slot="selected"
              >${this.selectedIcon}</ix-icon
            >`
          : nothing
      }
      </${tag}>`;
  }
}
